import "core-js/modules/es.array.push.js";
import { mixin as pluginMinx } from '@/plugin/index.min.js';
import EmapPlugin from './plugins';
import { format } from './utils';
import QuickSearch from './quick-search';
import ConditionList from './condition-list';
import Schema from './schema';
const {
  createRel,
  createOp,
  RELATION_TYPE,
  OP_TYPE
} = Schema;

const clearObj = obj => {
  Object.keys(obj).forEach(key => {
    obj[key] = '';
  });
};

export default {
  name: 'AdvSearch',
  components: {
    QuickSearch,
    ConditionList
  },
  mixins: [pluginMinx],
  EmapPlugin,
  props: {
    /**
     * 可定义组件展示方式，quick: 只使用快速搜索，adv: 只是用高级搜索，both: 同时支持
     */
    mode: {
      type: String,
      default: 'both'
    },

    /**
     * 待搜索的控件定义列表，参考 data-form 控件的 children 格式
     */
    controls: {
      type: Array,
      default: () => []
    },

    /**
     * 是否支持搜索方案保存功能
     */
    schema: {
      type: Boolean,
      default: false
    },

    /**
     * 高级查询表单域标签的宽度，会自动转换为px。
     */
    labelWidth: {
      type: String,
      default: '108'
    },

    /**
     * 表单域标签的位置，设置为top时标签会置于表单域的顶部，如果值为 left 或者 right 时，则表示左对齐、右对齐
     */
    labelPosition: {
      type: String,
      default: 'left'
    }
  },
  data: () => ({
    skeletonLoading: true,
    //骨架屏
    isAdv: false,
    // 是否展示为高级搜索
    advValue: {},
    // 高级搜索条件
    quickKeyword: '',
    // 快速搜索条件（输入框）
    quickValue: {},
    // 快速搜索条件（表单）
    selectedControls: [],
    // 已选需展示的条件，为 name 列表，如: ['age', 'name']
    allControls: [],
    // 所有可选条件，对控件对象列表
    schemaList: [] // 所有已保存的方案

  }),
  computed: {
    advView() {
      const {
        mode
      } = this;
      return mode === 'adv' || mode === 'both';
    },

    quickView() {
      const {
        mode
      } = this;
      return mode === 'quick' || mode === 'both';
    },

    quickInputControls() {
      return this.quickSearchControls.filter(item => !item.xtype);
    },

    quickSearchControls() {
      return this.formControls.filter(item => {
        return item.options.quickSearch;
      });
    },

    formControls() {
      return this.$applyPluginsWaterfall('format-controls', this.allControls) || [];
    }

  },

  created() {
    this.fetchControls();

    if (this.schema) {
      this.fetchSchemaList();
    }
  },

  methods: {
    handleQuickSearch() {
      /**
       * 点击搜索的时候触发此事件，参数为当前设置的搜索条件
       */
      this.$emit('search', this.getValue());
    },

    handleAdvSearch() {
      /**
       * 点击搜索的时候触发此事件，参数为当前设置的搜索条件
       */
      this.$emit('search', this.getValue());
    },

    handleConditionChange(value) {
      this.advValue = value;
      this.quickKeyword = value.keyword || '';
    },

    nomalizeValue(val, control) {
      if (Object.prototype.toString.call(val) === '[object Date]') {
        return format(val, control.options.format || 'yyyy-MM-dd');
      }

      return val;
    },

    getFormControlValue(controls = [], value = {}) {
      const opts = [];
      controls.forEach(control => {
        const {
          name,
          builder = 'include'
        } = control.options;
        const itemValue = value[name];

        if (itemValue) {
          // 处理范围值
          if (Array.isArray(itemValue) && control.options.range) {
            opts.push(createOp('lessEqual', {
              name,
              value: this.nomalizeValue(itemValue[1], control)
            }));
            opts.push(createOp('moreEqual', {
              name,
              value: this.nomalizeValue(itemValue[0], control)
            }));
          } else {
            opts.push(createOp(builder, {
              name,
              value: this.nomalizeValue(itemValue, control)
            }));
          }
        }
      });

      if (opts.length > 0) {
        return createRel(RELATION_TYPE.AND, {
          children: opts
        });
      }

      return null;
    },

    getInputControlValue(controls = [], keyword = '') {
      if (!keyword || controls.length === 0) {
        return null;
      }

      const opts = [];
      controls.forEach(control => {
        const {
          name
        } = control.options;
        opts.push(createOp(OP_TYPE.INCLUDE, {
          name,
          value: keyword
        }));
      });
      return createRel(RELATION_TYPE.OR, {
        children: opts
      });
    },

    getQuickValue() {
      const {
        quickInputControls,
        quickSearchControls,
        quickValue,
        quickKeyword
      } = this;
      return createRel(RELATION_TYPE.AND, {
        children: [this.getInputControlValue(quickInputControls, quickKeyword), this.getFormControlValue(quickSearchControls, quickValue)].filter(item => item)
      });
    },

    getAdvValue() {
      const {
        quickInputControls,
        formControls,
        advValue
      } = this;
      return createRel(RELATION_TYPE.AND, {
        children: [this.getInputControlValue(quickInputControls, advValue.keyword), this.getFormControlValue(formControls, advValue)].filter(item => item)
      });
    },

    /**
     * 获取当前搜索条件，返回格式 { type, value }，type 为 'quick/adv'，对应 value 为字符串或对象
     * @vuese
     */
    getValue() {
      const isAdv = this.isAdv;
      let value = isAdv ? this.getAdvValue() : this.getQuickValue();
      value = this.$applyPluginsWaterfall('post-value', value);

      if (Array.isArray(value)) {
        value = value.filter(item => item);

        if (value.length === 0) {
          return null;
        }
      }

      return value;
    },

    /**
     * 清除当前搜索条件
     * @vuese
     */
    clear() {
      clearObj(this.advValue);
      clearObj(this.quickValue);
      this.quickKeyword = '';
    },

    handleQuickWord(value) {
      this.quickKeyword = value;
      this.advValue.keyword = value;
    },

    handleQuickForm(value) {
      this.quickValue = value;
      this.$nextTick(() => {
        this.$emit('search', this.getValue());
      });
    },

    onSelectedChange(controls) {
      this.selectedControls = controls;
    },

    async fetchControls() {
      // 先从属性中获取列定义
      const {
        controls = []
      } = this;
      this.allControls = await this.$applyPluginsAsyncWaterfall('fetch-meta', controls);
    },

    async fetchSchemaList() {
      // 获取保存的方案
      this.schemaList = await this.$applyPluginsAsyncWaterfall('fetch-schemas', []);
    },

    handleSchemaChange(schema) {
      const {
        controls,
        value
      } = schema.data;
      this.selectedControls = [...controls];
      this.advValue = { ...value
      };
    },

    async handleSchemaAdd(params) {
      const value = { ...this.advValue
      };
      await this.$applyPluginsAsyncWaterfall('save-schema', { ...params,
        data: {
          controls: this.selectedControls,
          value
        }
      });
      this.$message({
        message: '方案保存成功',
        type: 'success'
      });
      await this.fetchSchemaList();
    },

    async handleSchemaSave(item) {
      await this.$applyPluginsAsyncWaterfall('save-schema', item);
      this.$message({
        message: '方案保存成功',
        type: 'success'
      });
      await this.fetchSchemaList();
    },

    async handleSchemaDel(item) {
      await this.$applyPluginsAsyncWaterfall('del-schema', item);
      this.$message({
        message: '方案删除成功',
        type: 'success'
      });
      await this.fetchSchemaList();
    }

  }
};